import { useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowLeftRight,
  BarChart3,
  Box,
  Boxes,
  CalendarClock,
  Check,
  ChevronRight,
  Flag,
  FlaskConical,
  Image as ImageIcon,
  Package,
  RotateCcw,
  ShieldCheck,
  Tag,
  TriangleAlert,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys } from '~/api/queryKeys';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import type { FileRoutesByTo } from '~/routeTree.gen';
import { cn } from '~/utils/cn';
import { diffInMs, nowISO } from '~/utils/date';

import type { RunProgress } from '../types/run.api.types';
import { STOPPED_RECENT_DAYS } from '../utils/batches-table';
import { useNavContext } from '../utils/nav-context';

/** One step of the trail; omit `to` on the last (current) step. */
export interface Crumb {
  label: ReactNode;
  /** A typed route path, e.g. '/catalog-enrichment/runs/$runId' — checked against the real route tree
   *  (`keyof FileRoutesByTo`) like every other `Link to=`/`navigate({ to })` call in the app, so
   *  a typo'd target fails to compile instead of only 404ing at runtime. */
  to?: keyof FileRoutesByTo;
  params?: Record<string, string>;
  /** Search params for the step's link (e.g. `{ stage: 'ENRICHMENT' }` for a decided review). */
  search?: Record<string, string>;
  /** Overrides the icon S2b would otherwise infer from `to` (e.g. a product's own thumbnail).
   *  Optional — every existing caller keeps working untouched without it. */
  icon?: ReactNode;
}

interface Props {
  items: Crumb[];
  /** 'chips' (default — catalog-enrichment's icon chips) or 'plain': quiet text links and a bold current
   *  step with no fills — the catalog export screens' prototype trail. */
  variant?: 'chips' | 'plain';
}

/** The 'plain' trail's step: text only, no fill or pill. */
const PLAIN_BASE =
  'inline-flex min-w-0 max-w-[16rem] items-center gap-1.5 text-[13px] leading-tight transition-colors';

/** Lucide icon per run-shell tab route segment (the 4th path segment under `/runs/$runId/…`) —
 *  "tabs [get] their own icon" (design round 3, S2b). */
const ICON_FOR_RUN_TAB: Record<string, LucideIcon> = {
  products: Package,
  gate: Flag,
  approval: ShieldCheck,
  compare: ArrowLeftRight,
  'content-results': BarChart3,
  insights: BarChart3,
  images: ImageIcon,
  issues: TriangleAlert,
  lineage: Workflow,
  recovery: RotateCcw,
};

/** Lucide icon for a catalog-enrichment section root / entity, keyed by the route's 2nd path segment. */
const ICON_FOR_SECTION: Record<string, LucideIcon> = {
  products: Package,
  experiments: FlaskConical,
  'feed-products': Tag,
  'needs-attention': TriangleAlert,
  schedules: CalendarClock,
};

/** A path (either a crumb's own `to` template, e.g. `/catalog-enrichment/runs/$runId/compare`, or the
 *  live resolved URL for the current step) split into its segments, ignoring empty leading/
 *  trailing slashes — route params and real ids both split the same way, so one matcher below
 *  handles a templated `to` and a resolved `pathname` alike. */
function pathSegments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

/**
 * S2b — the icon a breadcrumb chip shows, inferred purely from its route (`to`, or — for the
 * current/last step, which conventionally carries no `to` — the live URL) so every existing
 * caller keeps working untouched; `crumb.icon` always wins when a caller does want to override it
 * (e.g. a product's own thumbnail once a future track wires that up).
 */
function iconForCrumb(crumb: Crumb, isLast: boolean, currentPath: string): ReactNode | null {
  if (crumb.icon !== undefined) return crumb.icon;
  const path = crumb.to ?? (isLast ? currentPath : undefined);
  if (!path) return null;
  const segs = pathSegments(path);
  if (segs[0] !== 'catalog-enrichment' || !segs[1]) return null;
  const section = segs[1];
  let Icon: LucideIcon | undefined;
  if (section === 'runs') {
    if (segs.length === 2) Icon = Boxes;
    else if (segs.length === 3) Icon = Box;
    else Icon = segs[3] ? ICON_FOR_RUN_TAB[segs[3]] : undefined;
  } else {
    Icon = ICON_FOR_SECTION[section];
  }
  if (!Icon) return null;
  return <Icon aria-hidden="true" className="size-3.5 shrink-0" />;
}

/** A specific batch crumb's live state, read straight from the cached `RunProgress` (never a new
 *  fetch of its own — purely decorative chrome) — amber while a gate is open, red once the batch
 *  stopped on a problem, a neutral check once it finished clean; nothing for every other (still
 *  actively running) status, and nothing at all when the batch's progress isn't cached yet. */
const DAY_MS = 24 * 60 * 60 * 1000;

function runDotTone(progress: RunProgress | undefined): 'waiting' | 'failed' | 'done' | null {
  if (!progress) return null;
  if (progress.pendingGate) return 'waiting';
  if (['FAILED', 'TERMINATED', 'REJECTED'].includes(progress.runStatus)) {
    // The 7-day rule: an old stop is history — a neutral mark, not a red dot.
    const endedAt = progress.completedAt ?? null;
    const recent = !endedAt || diffInMs(endedAt, nowISO()) < STOPPED_RECENT_DAYS * DAY_MS;
    return recent ? 'failed' : 'done';
  }
  if (progress.runStatus === 'COMPLETED') return 'done';
  return null;
}

/** The batch crumb's live status dot (design round 3, S2b: "waiting amber, failed red, done
 *  neutral ✓"), from whatever `RunProgress` is already cached for it. */
function RunStatusDot({ runId }: { runId: string }) {
  const queryClient = useQueryClient();
  const progress = queryClient.getQueryData<RunProgress>(
    queryKeys.catalogService.runProgress(runId),
  );
  const tone = runDotTone(progress);
  if (!tone) return null;
  if (tone === 'done') {
    return <Check aria-hidden="true" className="text-muted-foreground size-3 shrink-0" />;
  }
  return (
    <span
      aria-hidden="true"
      className={cn(
        'size-1.5 shrink-0 rounded-full',
        tone === 'waiting' ? 'bg-warning' : 'bg-error',
      )}
    />
  );
}

/** A specific-batch crumb's status dot, or `null` when this chip isn't one (only `/runs/$runId`
 *  itself, not its tabs, carries a run-wide status). `routeRunId` is the live route's own
 *  `$runId` param — the fallback for the current step, which (per run-shell's Overview crumb)
 *  often has no `to`/`params` of its own at all. */
function statusDotForCrumb(
  crumb: Crumb,
  isLast: boolean,
  currentPath: string,
  routeRunId: string | undefined,
): ReactNode | null {
  const path = crumb.to ?? (isLast ? currentPath : undefined);
  if (!path) return null;
  const segs = pathSegments(path);
  if (segs[0] !== 'catalog-enrichment' || segs[1] !== 'runs' || segs.length !== 3) return null;
  const runId = crumb.params?.runId ?? (isLast ? routeRunId : undefined);
  return runId ? <RunStatusDot runId={runId} /> : null;
}

/** Shared chip look: a quiet muted pill for an earlier/non-linking step (hover lift when it is a
 *  link), a tinted bold pill for the current step — design round 3, S2b ("icon chips"). */
const CHIP_BASE =
  'inline-flex min-w-0 max-w-[16rem] items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-all';

/** One breadcrumb step: icon + label (+ a batch's live status dot), as a Link for an earlier step
 *  with a route, a plain chip otherwise (the current step, or a step with nothing to link to). */
function BreadcrumbChip({
  crumb,
  isLast,
  currentPath,
  routeRunId,
  variant,
}: {
  crumb: Crumb;
  isLast: boolean;
  currentPath: string;
  routeRunId: string | undefined;
  variant: 'chips' | 'plain';
}) {
  const plain = variant === 'plain';
  const icon = iconForCrumb(crumb, isLast, currentPath);
  const dot = statusDotForCrumb(crumb, isLast, currentPath, routeRunId);
  const content = (
    <>
      {icon}
      <span className="truncate">{crumb.label}</span>
      {dot}
    </>
  );
  if (isLast || !crumb.to) {
    return (
      <span
        aria-current={isLast ? 'page' : undefined}
        className={cn(
          plain ? PLAIN_BASE : CHIP_BASE,
          isLast
            ? plain
              ? 'text-foreground font-semibold'
              : 'bg-primary/10 text-primary font-medium'
            : 'text-muted-foreground',
        )}
      >
        {content}
      </span>
    );
  }
  return (
    <Link
      to={crumb.to}
      params={crumb.params as never}
      search={crumb.search as never}
      className={cn(
        plain
          ? cn(PLAIN_BASE, 'text-muted-foreground hover:text-foreground cursor-pointer font-medium')
          : cn(
              CHIP_BASE,
              'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer hover:-translate-y-px',
            ),
      )}
    >
      {content}
    </Link>
  );
}

/** How many steps show before a long trail collapses its middle into one "…" menu — the current
 *  step and the one right before it (the ones a user re-orients from) always stay visible. */
const MAX_VISIBLE_CRUMBS = 4;

/** The "…" chip for a long trail's collapsed middle steps: a menu listing each hidden step as a
 *  link (design round 3, S2b — "middle collapses to '…' that opens a menu on CLICK"). */
function CollapsedCrumbsMenu({ items }: { items: Crumb[] }) {
  const { t } = useTranslation('catalog-enrichment');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t('breadcrumbs.showHidden')}
          className={cn(CHIP_BASE, 'text-muted-foreground hover:bg-muted h-auto cursor-pointer')}
        >
          …
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {items.map((c, i) =>
          c.to ? (
            <DropdownMenuItem key={i} asChild className="cursor-pointer">
              <Link to={c.to} params={c.params as never} search={c.search as never}>
                {c.label}
              </Link>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem key={i} disabled>
              {c.label}
            </DropdownMenuItem>
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Where the user is (Q27): Section › entity › sub-page, on every catalog-enrichment page below a
 * section root. Earlier steps are quiet linked chips, the current step a tinted bold chip
 * (design round 3, S2b — "icon chips"); each chip's icon (and a batch's live waiting/failed/done
 * dot) is inferred purely from the route, so run-shell / entity-header's existing callers keep
 * working untouched. A long trail collapses its middle steps into one "…" menu; the trail always
 * wraps rather than scrolling at any width.
 */
export function PageBreadcrumbs({ items, variant = 'chips' }: Props) {
  const { t } = useTranslation('catalog-enrichment');
  const location = useLocation();
  // The live route's own params (e.g. `runId`) — the fallback for a current step that (like
  // run-shell's Overview crumb) carries no `to`/`params` of its own to infer a status dot from.
  const routeParams = useParams({ strict: false });

  const collapse = items.length > MAX_VISIBLE_CRUMBS;
  const head = collapse ? items.slice(0, 1) : items;
  const hidden = collapse ? items.slice(1, items.length - 2) : [];
  const tail = collapse ? items.slice(items.length - 2) : [];

  const renderChip = (crumb: Crumb, index: number) => (
    <li key={index} className="flex min-w-0 items-center gap-1">
      {index > 0 ? <ChevronRight aria-hidden="true" className="size-3.5 shrink-0" /> : null}
      <BreadcrumbChip
        crumb={crumb}
        isLast={index === items.length - 1}
        currentPath={location.pathname}
        routeRunId={routeParams.runId}
        variant={variant}
      />
    </li>
  );

  return (
    <nav aria-label={t('breadcrumbs.label')} className="text-muted-foreground text-sm">
      <ol className="flex min-w-0 flex-wrap items-center gap-1">
        {head.map((c, i) => renderChip(c, i))}
        {collapse ? (
          <li className="flex min-w-0 items-center gap-1">
            <ChevronRight aria-hidden="true" className="size-3.5 shrink-0" />
            <CollapsedCrumbsMenu items={hidden} />
          </li>
        ) : null}
        {tail.map((c, i) => renderChip(c, head.length + hidden.length + i))}
      </ol>
    </nav>
  );
}

/**
 * S8 — "← Back to <origin>" chip: shown on any destination route opened with a `from` (product,
 * listing, product-in-batch, needs attention, experiment, compare), restoring the EXACT origin
 * page (its tab and whatever filters it had) when the link that brought the user here recorded
 * one (`back`); otherwise falls back to the origin's own base route. Renders nothing opened
 * without `from` (bookmark / main nav) — today's behaviour, unchanged. An imperative `navigate`
 * (not a `Link`) because the typed `Link` component has no way to express "navigate to this
 * already-built href" — the router's own `navigate({ href })` parses it into `to`/`search` and
 * still does a client-side transition (never a full reload) for an internal path like this one.
 */
export function BackToOriginChip() {
  const { back } = useNavContext();
  const navigate = useNavigate();
  if (!back) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground h-auto w-fit cursor-pointer gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
      onClick={() => {
        void navigate(
          back.href
            ? ({ href: back.href } as never)
            : ({ to: back.to, params: back.params } as never),
        );
      }}
    >
      <ArrowLeft aria-hidden="true" className="size-3.5 shrink-0" />
      <span className="truncate">{back.label}</span>
    </Button>
  );
}
